import { useEffect, useState } from 'react';
import { useAccountApi } from '../../api/AccountApiProvider';
import { toApiError } from '../../api/errors';
import type { ApiError } from '../../api/errors';
import type { RolloutBoard } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { AttributionNotice } from '../../ui/AttributionNotice';
import { SelectField, TextField } from '../../ui/form';
import { Button, Chip } from '../../ui/primitives';
import { Modal } from '../../ui/Modal';
import { firstWave, waveLabelKey, waveMembers, waveOrder } from './rollout';

/**
 * Starting a rollout, with what it will move in front of whoever starts it.
 *
 * A rollout is not a switch. Pressing this reconciles other people's clusters,
 * one at a time, under the name of whoever pressed it — so the dialog says which
 * tenancies move now, in the order they will move, and that the rest of the
 * fleet does not follow until somebody signs this wave off.
 *
 * What it does **not** do is forecast. Which named breaks the move crosses is
 * worked out by the server from what the fleet is seen running, against a list
 * of breaks that lives there; a second copy of that list in this console would
 * be a second thing to keep true. The forecast appears on the board as soon as
 * the rollout exists, which is before any wave has finished.
 */
export function StartRolloutDialog({
  board,
  actorName,
  onClose,
  onStarted,
}: {
  board: RolloutBoard;
  actorName: string;
  onClose: () => void;
  onStarted: (board: RolloutBoard) => void;
}) {
  const { t } = useI18n();
  const api = useAccountApi();

  const [target, setTarget] = useState('');
  const [tried, setTried] = useState(false);
  // What the registry holds, when this deployment has one. `null` while it is
  // being asked: the field is drawn as a text box until the answer arrives, so
  // a slow registry delays nothing — it only replaces the box with a list once
  // it can.
  const [offered, setOffered] = useState<string[] | null>(null);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    void api.provider
      .offeredVersions()
      .then((answer) => {
        if (live && answer.available && answer.versions.length > 0) setOffered(answer.versions);
      })
      // A registry that cannot be listed is not this dialog's problem to
      // report: the text field still works, and the server has already logged
      // why. Surfacing it here would put an error in front of somebody who can
      // do nothing about it and does not need to.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [api]);

  const first = firstWave(board);
  const moving = first === undefined ? [] : waveMembers(board, first.wave);
  const after = waveOrder(board).slice(1);

  const missing = target.trim() === '';

  async function start(): Promise<void> {
    setTried(true);
    if (missing) return;

    setFailure(null);
    setBusy(true);
    try {
      onStarted(await api.provider.startRollout(target.trim()));
    } catch (error: unknown) {
      setFailure(toApiError(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('rolloutStart.title')}
      subtitle={t('rolloutStart.subtitle')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <span className="spacer" />
          <Button variant="primary" disabled={busy} onClick={() => void start()}>
            {busy ? `${t('rolloutStart.submitting')}…` : t('rolloutStart.submit')}
          </Button>
        </>
      }
    >
      <div className="stack">
        {failure !== null && <StartFailure failure={failure} />}

        {offered === null ? (
          <TextField
            label={t('rolloutStart.target.label')}
            hint={t('rolloutStart.target.hint')}
            mono
            value={target}
            {...(tried && missing ? { error: t('rolloutStart.check.required') } : {})}
            onChange={(event) => {
              setTarget(event.target.value);
            }}
          />
        ) : (
          <SelectField
            label={t('rolloutStart.target.label')}
            hint={t('rolloutStart.target.fromRegistry')}
            value={target}
            {...(tried && missing ? { error: t('rolloutStart.check.required') } : {})}
            onChange={(event) => {
              setTarget(event.target.value);
            }}
          >
            <option value="">{t('rolloutStart.target.choose')}</option>
            {offered.map((version) => (
              <option key={version} value={version}>
                {version}
              </option>
            ))}
          </SelectField>
        )}

        <p className="hint">{t('rolloutStart.target.floating')}</p>

        {first !== undefined && (
          <Alert
            tone={moving.length === 0 ? 'warn' : 'info'}
            title={t('rolloutStart.willMove.title', { wave: t(waveLabelKey(first.wave)) })}
          >
            {moving.length === 0 ? (
              <p>{t('rolloutStart.willMove.none')}</p>
            ) : (
              <>
                <p>{t('rolloutStart.willMove.order')}</p>
                <div className="row" style={{ marginTop: 8 }}>
                  {moving.map((row) => (
                    <Chip key={row.slug}>{row.slug}</Chip>
                  ))}
                </div>
              </>
            )}
          </Alert>
        )}

        <Alert tone="neutral" title={t('rolloutStart.then.title')}>
          <p>{t('rolloutStart.then.body')}</p>
          {after.length > 0 && (
            <div className="row" style={{ marginTop: 8 }}>
              {after.map((wave) => (
                <Chip key={wave.wave}>{t(waveLabelKey(wave.wave))}</Chip>
              ))}
            </div>
          )}
          <p style={{ marginTop: 8 }}>{t('rolloutStart.forecastLater')}</p>
        </Alert>

        <AttributionNotice actorName={actorName} />
      </div>
    </Modal>
  );
}

/**
 * The one refusal worth its own words.
 *
 * `conflict` here means one of two things the server names in its own sentence:
 * a rollout is already under way, or the first wave holds nobody. Both are
 * shown as that sentence rather than as the generic wording, because both are
 * about this fleet at this moment.
 */
function StartFailure({ failure }: { failure: ApiError }) {
  const { t } = useI18n();

  if (failure.code === 'conflict') {
    return (
      <Alert tone="deny" title={t('rolloutStart.refused.title')} live>
        <p className="mono">{failure.message}</p>
        <p style={{ marginTop: 8 }}>{t('rolloutStart.refused.body')}</p>
      </Alert>
    );
  }
  return <ErrorAlert error={failure} />;
}
